import React, { useEffect, useState, useMemo } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import Select from 'react-select';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

export default function SalaryHeadList() {
    const { http, user } = AuthUser();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [salaryMasters, setSalaryMasters] = useState([]);
    const [masterHeads, setMasterHeads] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState([]);
    const [selectedEmpType, setSelectedEmpType] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null); // Individual employee detail view
    const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState([]); // Multi-select filter
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    
    // Store head amounts and working days for each employe
    const [employeeData, setEmployeeData] = useState({});
    const [generatingFor, setGeneratingFor] = useState(null);
    
    // For individual employee view - leaves
    const [employeeLeaves, setEmployeeLeaves] = useState([]);
    const [loadingLeaves, setLoadingLeaves] = useState(false);

    // Emp types from API
    const [empTypes, setEmpTypes] = useState([]);

    // Track already generated salaries for the selected month
    const [generatedSalaries, setGeneratedSalaries] = useState([]);

    // Track which generated rows are in edit mode
    const [editingRows, setEditingRows] = useState({});

    useEffect(() => {
        fetchSalaryMasters();
        fetchMasterHeads();
        fetchDepartments();
        fetchEmpTypes();
    }, []);

    // Fetch generated salaries when month changes
    useEffect(() => {
        if (selectedMonth) {
            fetchGeneratedSalaries();
        }
    }, [selectedMonth]);

    // Initialize employee data when salary masters or heads change
    useEffect(() => {
        const initialData = {};
        salaryMasters.forEach(sm => {
            initialData[sm.id] = {
                workingDays: parseInt(sm.working_days) || 30,
                leaveDays: 0,
                heads: {}
            };
            masterHeads.forEach(head => {
                initialData[sm.id].heads[head.id] = 0;
            });
        });
        setEmployeeData(initialData);
    }, [salaryMasters, masterHeads]);

    // Fetch leaves when individual employee is selected
    useEffect(() => {
        if (selectedEmployee && selectedMonth) {
            fetchEmployeeLeaves();
        } else {
            setEmployeeLeaves([]);
        }
    }, [selectedEmployee, selectedMonth]);

    const fetchEmployeeLeaves = async () => {
        const emp = salaryMasters.find(sm => sm.id === selectedEmployee?.value);
        if (!emp?.user_id || !selectedMonth) return;
        
        setLoadingLeaves(true);
        try {
            const [year, month] = selectedMonth.split('-');
            const response = await http.get(`/leave_applied?staff_id=${emp.user_id}&company_id=${user.company_id}`);
            if (response.data.success) {
                const filtered = response.data.data.filter(leave => {
                    const leaveDate = moment(leave.leave_applied);
                    return leaveDate.year() === parseInt(year) && 
                           (leaveDate.month() + 1) === parseInt(month) &&
                           leave.is_approved === 1;
                });
                setEmployeeLeaves(filtered);
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
        } finally {
            setLoadingLeaves(false);
        }
    };

    const fetchGeneratedSalaries = async () => {
        try {
            const [year, month] = selectedMonth.split('-');
            const response = await http.get(`/generated_salaries?login_user=${user.id}&month=${month}&year=${year}`);
            if (response.data.success) {
                setGeneratedSalaries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching generated salaries:', error);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await http.get(`/departments?login_user=${user.id}`);
            if (response.data.success) {
                setDepartments(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    const fetchEmpTypes = async () => {
        try {
            const response = await http.get(`/emp_types?login_user=${user.id}`);
            if (response.data.success) {
                setEmpTypes(response.data.data.map(et => et.name));
            }
        } catch (error) {
            console.error('Error fetching employee types:', error);
        }
    };

    const fetchMasterHeads = async () => {
        try {
            const response = await http.get(`/salary_master_heads?login_user=${user.id}`);
            if (response.data.success) {
                setMasterHeads(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching salary heads:', error);
        }
    };

    const fetchSalaryMasters = async () => {
        try {
            const response = await http.get(`/salary_masters?login_user=${user.id}`);
            if (response.data.success) {
                setSalaryMasters(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching salary masters:', error);
        } finally {
            setLoading(false);
        }
    };

    const departmentOptions = useMemo(() => {
        return departments.map(d => ({ value: d.id, label: d.name }));
    }, [departments]);

    const empTypeOptions = useMemo(() => {
        return empTypes.map(t => ({ value: t, label: t }));
    }, [empTypes]);

    const employeeOptions = useMemo(() => {
        let filtered = salaryMasters;
        if (selectedDepartment?.length > 0) {
            const deptIds = selectedDepartment.map(d => d.value);
            filtered = filtered.filter(sm => deptIds.includes(sm.user?.department_id));
        }
        if (selectedEmpType?.length > 0) {
            const types = selectedEmpType.map(t => t.value);
            filtered = filtered.filter(sm => types.includes(sm.emp_type));
        }
        return filtered.map(sm => ({
            value: sm.id,
            label: sm.user?.name + (sm.user?.last_name ? ' ' + sm.user?.last_name : ''),
            data: sm
        }));
    }, [salaryMasters, selectedDepartment, selectedEmpType]);

    const monthOptions = useMemo(() => {
        const options = [];
        const now = new Date();
        for (let i = -12; i <= 2; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            options.push({ value, label });
        }
        return options.reverse();
    }, []);

    // Filter employees by department, emp type, and selected employees (for table view)
    const filteredEmployees = useMemo(() => {
        let filtered = salaryMasters;
        if (selectedDepartment?.length > 0) {
            const deptIds = selectedDepartment.map(d => d.value);
            filtered = filtered.filter(sm => deptIds.includes(sm.user?.department_id));
        }
        if (selectedEmpType?.length > 0) {
            const types = selectedEmpType.map(t => t.value);
            filtered = filtered.filter(sm => types.includes(sm.emp_type));
        }
        if (selectedEmployeeFilter?.length > 0) {
            const empIds = selectedEmployeeFilter.map(e => e.value);
            filtered = filtered.filter(sm => empIds.includes(sm.id));
        }
        return filtered;
    }, [salaryMasters, selectedDepartment, selectedEmpType, selectedEmployeeFilter]);

    const additionHeads = useMemo(() => masterHeads.filter(h => h.action === 'addition'), [masterHeads]);
    const deductionHeads = useMemo(() => masterHeads.filter(h => h.action === 'deduction'), [masterHeads]);

    // Statutory deductions prorated from Gross Salary (E) basis
    // masterDays = working_days from salary master, actualDays = editable "Days" in the table
    // perDay = fullMonthAmount / masterDays, proratedAmount = perDay * actualDays
    const getStatutoryDeductions = (emp, workingDays) => {
        const deductions = [];
        const masterDays = parseInt(emp?.working_days) || 30;
        const actualDays = parseInt(workingDays) || masterDays;
        if (parseFloat(emp?.total_pf_employee) > 0) {
            const fullAmt = parseFloat(emp.total_pf_employee) || 0;
            const perDay = fullAmt / masterDays;
            deductions.push({ id: 'pf_emp', name: 'PF Contribution (Employee)', amount: perDay * actualDays, fullAmount: fullAmt, formula: emp.pf_employee_formula, perDay, masterDays, days: actualDays });
        }
        if (parseFloat(emp?.total_esi_employee) > 0) {
            const fullAmt = parseFloat(emp.total_esi_employee) || 0;
            const perDay = fullAmt / masterDays;
            deductions.push({ id: 'esi_emp', name: 'ESI Contribution (Employee)', amount: perDay * actualDays, fullAmount: fullAmt, formula: emp.esi_employee_formula, perDay, masterDays, days: actualDays });
        }
        if (emp?.pt_enabled && parseFloat(emp.total_pt) > 0) {
            const fullAmt = parseFloat(emp.total_pt) || 0;
            const perDay = fullAmt / masterDays;
            deductions.push({ id: 'pt', name: 'Professional Tax', amount: perDay * actualDays, fullAmount: fullAmt, formula: emp.pt_formula, perDay, masterDays, days: actualDays });
        }
        if (parseFloat(emp?.total_tds_deduction) > 0) {
            const fullAmt = parseFloat(emp.total_tds_deduction) || 0;
            const perDay = fullAmt / masterDays;
            deductions.push({ id: 'tds', name: 'Tax Deduction at Source', amount: perDay * actualDays, fullAmount: fullAmt, formula: emp.tds_deduction_formula, perDay, masterDays, days: actualDays });
        }
        return deductions;
    };

    // Check if salary is already generated for an employee
    const isAlreadyGenerated = (userId) => {
        return generatedSalaries.some(gs => gs.user_id === userId);
    };

    // Get generated salary details for an employee
    const getGeneratedSalary = (userId) => {
        return generatedSalaries.find(gs => gs.user_id === userId);
    };

    // Toggle edit mode for a generated row - load generated data back into inputs
    const handleEditRow = (empId, userId) => {
        const generatedData = getGeneratedSalary(userId);
        const emp = salaryMasters.find(sm => sm.id === empId);
        if (generatedData) {
            const headsData = {};
            try {
                const savedHeads = JSON.parse(generatedData.salary_heads || '[]');
                savedHeads.forEach(h => {
                    if (!h.is_statutory) {
                        const matchingHead = masterHeads.find(mh => mh.id === h.head_id);
                        if (matchingHead) {
                            headsData[matchingHead.id] = h.amount || 0;
                        }
                    }
                });
            } catch (e) {
                console.error('Error parsing salary heads:', e);
            }

            const savedLeaveDays = parseInt(generatedData.leave_days) || 0;
            setEmployeeData(prev => ({
                ...prev,
                [empId]: {
                    workingDays: generatedData.number_working_day || parseInt(emp?.working_days) || 30,
                    leaveDays: savedLeaveDays,
                    heads: { ...prev[empId]?.heads, ...headsData }
                }
            }));
        }
        setEditingRows(prev => ({ ...prev, [empId]: true }));
    };

    // Cancel edit mode for a row
    const handleCancelEdit = (empId) => {
        setEditingRows(prev => ({ ...prev, [empId]: false }));
    };

    // Check if a row is in edit mode
    const isEditing = (empId) => !!editingRows[empId];

    // Get selected employee's salary master data
    const selectedMaster = useMemo(() => {
        if (!selectedEmployee) return null;
        return salaryMasters.find(sm => sm.id === selectedEmployee.value);
    }, [selectedEmployee, salaryMasters]);

    const preventScrollChange = (e) => e.target.blur();

    // Update leave days for an employee — auto-recalculates working days
    const handleLeaveDaysChange = (empId, value) => {
        const emp = salaryMasters.find(sm => sm.id === empId);
        const masterDays = parseInt(emp?.working_days) || 30;
        const leave = parseInt(value) || 0;
        setEmployeeData(prev => ({
            ...prev,
            [empId]: {
                ...prev[empId],
                leaveDays: leave,
                workingDays: Math.max(0, masterDays - leave)
            }
        }));
    };

    // Update working days directly (kept for backwards compat)
    const handleWorkingDaysChange = (empId, value) => {
        setEmployeeData(prev => ({
            ...prev,
            [empId]: {
                ...prev[empId],
                workingDays: parseInt(value) || 0
            }
        }));
    };

    // Update head amount for an employee
    const handleHeadAmountChange = (empId, headId, value) => {
        setEmployeeData(prev => ({
            ...prev,
            [empId]: {
                ...prev[empId],
                heads: {
                    ...prev[empId]?.heads,
                    [headId]: parseFloat(value) || 0
                }
            }
        }));
    };

    // Calculate totals for an employee
    const calculateEmployeeTotals = (empId) => {
        const emp = salaryMasters.find(sm => sm.id === empId);
        if (!emp) return { additions: 0, deductions: 0, netSalary: 0, statutoryDeductions: 0 };

        const empData = employeeData[empId] || { workingDays: parseInt(emp?.working_days) || 30, heads: {} };
        const perDaySalary = parseFloat(emp.per_day_salary) || 0;
        const workingDays = empData.workingDays || 0;
        const baseAmount = perDaySalary * workingDays;
        
        let additions = 0;
        let deductions = 0;

        masterHeads.forEach(head => {
            const amount = parseFloat(empData.heads?.[head.id]) || 0;
            if (head.action === 'addition') {
                additions += amount;
            } else {
                deductions += amount;
            }
        });

        // Add statutory deductions from salary master
        const statutory = getStatutoryDeductions(emp, workingDays);
        const statutoryDeductions = statutory.reduce((sum, item) => sum + item.amount, 0);
        deductions += statutoryDeductions;

        const netSalary = baseAmount + additions - deductions;

        return { baseAmount, additions, deductions, netSalary, perDaySalary, workingDays, statutoryDeductions };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getMonthLabel = () => {
        if (!selectedMonth) return '';
        const [year, month] = selectedMonth.split('-');
        const date = new Date(year, parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    // Generate invoice for a single employee
    const handleGenerateInvoice = async (empId) => {
        const emp = salaryMasters.find(sm => sm.id === empId);
        if (!emp) return;

        const empData = employeeData[empId];
        const totals = calculateEmployeeTotals(empId);

        setGeneratingFor(empId);
        try {
            const [year, month] = selectedMonth.split('-');
            
            // Custom salary heads breakdown
            const headsBreakdown = masterHeads
                .filter(head => (parseFloat(empData?.heads?.[head.id]) || 0) > 0)
                .map(head => ({
                    head_id: head.id,
                    name: head.name,
                    action: head.action,
                    amount: parseFloat(empData?.heads?.[head.id]) || 0
                }));

            // Add statutory deductions from salary master (PF Emp, ESI Emp, PT, TDS)
            const statutory = getStatutoryDeductions(emp, empData?.workingDays || parseInt(emp?.working_days) || 30);
            statutory.forEach(item => {
                headsBreakdown.push({
                    head_id: item.id,
                    name: item.name,
                    action: 'deduction',
                    amount: item.amount,
                    formula: item.formula,
                    per_day: item.perDay,
                    working_days: item.days,
                    is_statutory: true
                });
            });

            const basic = parseFloat(emp.basic_salary) || 0;
            const daPercent = parseFloat(emp.da_per) || 0;
            const hraPercent = parseFloat(emp.hra_per) || 0;
            const tdsPercent = parseFloat(emp.tds_per) || 0;

            const requestData = {
                login_user: user.id,
                user_id: emp.user_id,
                salary_master_id: emp.id,
                month: parseInt(month),
                year: parseInt(year),
                basic_salary: basic,
                per_day_salary: totals.perDaySalary,
                number_working_day: totals.workingDays,
                leave_days: empData.leaveDays || 0,
                da_per: daPercent,
                da_amount: (basic * daPercent) / 100,
                hra_per: hraPercent,
                hra_amount: (basic * hraPercent) / 100,
                tds_per: tdsPercent,
                tds_amount: (basic * tdsPercent) / 100,
                formula: emp.formula || 'A+B+C-D',
                gross_salary: parseFloat(emp.gross_salary) || basic,
                total_additions: totals.additions,
                total_deductions: totals.deductions,
                net_salary: totals.netSalary,
                salary_heads: JSON.stringify(headsBreakdown),
                status: 'generated'
            };

            const response = await http.post('/generated_salary', requestData);

            if (response.data.success) {
                toast.success(response.data.message || `Invoice generated for ${emp.user?.name}!`);
                // Refresh generated salaries and wait for it to complete
                await fetchGeneratedSalaries();
                // Exit edit mode for this row
                setEditingRows(prev => ({ ...prev, [empId]: false }));
            } else {
                toast.error(response.data.message || 'Failed to generate invoice');
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            toast.error(error.response?.data?.message || 'Error generating invoice');
        } finally {
            setGeneratingFor(null);
        }
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-3 px-3">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                <i className="fa fa-file-invoice me-2"></i>
                        Generate Salary Invoice
                            </h5>
                    <span className="badge bg-primary fs-6">{getMonthLabel()}</span>
                        </div>

                {/* Filters */}
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Month</label>
                                <Select
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === selectedMonth)}
                                    onChange={(opt) => setSelectedMonth(opt ? opt.value : '')}
                                    placeholder="Select Month"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Department</label>
                                <Select
                                    options={departmentOptions}
                                    value={selectedDepartment}
                                    onChange={(opts) => setSelectedDepartment(opts || [])}
                                    placeholder="All Departments"
                                    isMulti
                                    isClearable
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        multiValue: (base) => ({ ...base, maxWidth: '120px' })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Emp Type</label>
                                <Select
                                    options={empTypeOptions}
                                    value={selectedEmpType}
                                    onChange={(opts) => setSelectedEmpType(opts || [])}
                                    placeholder="All Types"
                                    isMulti
                                    isClearable
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        multiValue: (base) => ({ ...base, maxWidth: '100px' })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Filter Employees</label>
                                <Select
                                    options={employeeOptions}
                                    value={selectedEmployeeFilter}
                                    onChange={(opts) => setSelectedEmployeeFilter(opts || [])}
                                    placeholder="All Employees"
                                    isMulti
                                    isClearable
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        multiValue: (base) => ({ ...base, maxWidth: '120px' })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className="col-md-3">
                                <span className="badge bg-secondary">{filteredEmployees.length} Employees</span>
                                {(selectedDepartment?.length > 0 || selectedEmpType?.length > 0 || selectedEmployeeFilter?.length > 0) && (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary ms-2"
                                        onClick={() => {
                                            setSelectedDepartment([]);
                                            setSelectedEmpType([]);
                                            setSelectedEmployeeFilter([]);
                                        }}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Salary Heads Legend */}
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                            <strong className="me-2">Salary Heads:</strong>
                            {additionHeads.map(h => (
                                <span key={h.id} className="badge bg-success">{h.name} (+)</span>
                            ))}
                            {deductionHeads.map(h => (
                                <span key={h.id} className="badge bg-danger">{h.name} (-)</span>
                            ))}
                            <span className="ms-3 text-muted">|</span>
                            <strong className="ms-2 text-muted">Statutory:</strong>
                            <span className="badge" style={{ background: '#dc3545', opacity: 0.8 }}>PF Emp (-)</span>
                            <span className="badge" style={{ background: '#dc3545', opacity: 0.8 }}>ESI Emp (-)</span>
                            <span className="badge" style={{ background: '#dc3545', opacity: 0.8 }}>PT (-)</span>
                            <span className="badge" style={{ background: '#dc3545', opacity: 0.8 }}>TDS (-)</span>
                            <small className="text-muted ms-2">(Auto from Salary Master + per day shown)</small>
                        </div>
                    </div>
                </div>

                {/* INDIVIDUAL EMPLOYEE VIEW */}
                {selectedEmployee && selectedMaster && (() => {
                    const empAlreadyGenerated = isAlreadyGenerated(selectedMaster.user_id);
                    const empGeneratedData = getGeneratedSalary(selectedMaster.user_id);
                    
                    return (
                    <div className="card mb-3">
                        <div className="card-header" style={{ background: empAlreadyGenerated ? '#d1e7dd' : '#e3f2fd' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <i className="fa fa-user me-2"></i>
                                    {selectedMaster.user?.name} {selectedMaster.user?.last_name || ''} 
                                    <span className="text-muted ms-2">({selectedMaster.user?.department?.name || '-'})</span>
                                </h6>
                                <div className="d-flex align-items-center gap-2">
                                    {empAlreadyGenerated && (
                                        <span className="badge bg-success">
                                            <i className="fa fa-check-circle me-1"></i>
                                            Invoice Already Generated for {getMonthLabel()}
                                        </span>
                                    )}
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedEmployee(null)} title="Close detail view">
                                        <i className="fa fa-times"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Employee Info & Working Days */}
                            <div className="row mb-3">
                                <div className="col-md-2">
                                    <small className="text-muted">Per Day Salary</small>
                                    <h5 className="text-primary mb-0">{formatCurrency(selectedMaster.per_day_salary)}</h5>
                                </div>
                                <div className="col-md-1">
                                    <label className="form-label small mb-1">LOP Days</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={empAlreadyGenerated ? (empGeneratedData?.leave_days || 0) : (employeeData[selectedMaster.id]?.leaveDays || '')}
                                        onChange={(e) => handleLeaveDaysChange(selectedMaster.id, e.target.value)}
                                        onWheel={preventScrollChange}
                                        min="0"
                                        max={parseInt(selectedMaster?.working_days) || 31}
                                        disabled={empAlreadyGenerated}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="col-md-1">
                                    <small className="text-muted">Working Days</small>
                                    <h5 className="mb-0" style={{ color: (employeeData[selectedMaster.id]?.leaveDays > 0) ? '#e67e22' : '#333' }}>
                                        {empAlreadyGenerated ? (empGeneratedData?.number_working_day || 0) : (employeeData[selectedMaster.id]?.workingDays || parseInt(selectedMaster?.working_days) || 30)}
                                        {!empAlreadyGenerated && employeeData[selectedMaster.id]?.leaveDays > 0 && (
                                            <small className="d-block" style={{ fontSize: '10px', color: '#e67e22' }}>
                                                {parseInt(selectedMaster?.working_days) || 30}-{employeeData[selectedMaster.id]?.leaveDays}
                                            </small>
                                        )}
                                    </h5>
                                </div>
                                <div className="col-md-2">
                                    <small className="text-muted">Base Amount</small>
                                    <h5 className="text-info mb-0">
                                        {formatCurrency((parseFloat(selectedMaster.per_day_salary) || 0) * (employeeData[selectedMaster.id]?.workingDays || 0))}
                                    </h5>
                                </div>
                                <div className="col-md-2">
                                    <small className="text-muted">Additions</small>
                                    <h5 className="text-success mb-0">+{formatCurrency(calculateEmployeeTotals(selectedMaster.id).additions)}</h5>
                                </div>
                                <div className="col-md-2">
                                    <small className="text-muted">Deductions</small>
                                    <h5 className="text-danger mb-0">-{formatCurrency(calculateEmployeeTotals(selectedMaster.id).deductions)}</h5>
                                </div>
                                <div className="col-md-2">
                                    <small className="text-muted">Net Salary</small>
                                    <h4 className="text-success mb-0">{formatCurrency(calculateEmployeeTotals(selectedMaster.id).netSalary)}</h4>
                                </div>
                            </div>

                            {/* Salary Heads Input */}
                            <div className="row mb-3">
                                {/* Additions */}
                                <div className="col-md-6">
                                    <div className="card border-success h-100">
                                        <div className="card-header py-2" style={{ background: '#d4edda' }}>
                                            <h6 className="mb-0 text-success"><i className="fa fa-plus-circle me-2"></i>Additions</h6>
                                        </div>
                                        <div className="card-body p-2">
                                            {additionHeads.length === 0 ? (
                                                <p className="text-muted text-center mb-0">No addition heads</p>
                                            ) : (
                                                <table className="table table-sm mb-0">
                                                    <tbody>
                                                        {additionHeads.map(head => (
                                                            <tr key={head.id}>
                                                                <td><strong>{head.name}</strong></td>
                                                                <td style={{ width: '150px' }}>
                                                                    <div className="input-group input-group-sm">
                                                                        <span className="input-group-text bg-success text-white">₹</span>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            value={employeeData[selectedMaster.id]?.heads?.[head.id] || ''}
                                                                            onChange={(e) => handleHeadAmountChange(selectedMaster.id, head.id, e.target.value)}
                                                                            onWheel={preventScrollChange}
                                                                            placeholder="0"
                                                                            disabled={empAlreadyGenerated}
                                        />
                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Deductions */}
                                <div className="col-md-6">
                                    <div className="card border-danger h-100">
                                        <div className="card-header py-2" style={{ background: '#f8d7da' }}>
                                            <h6 className="mb-0 text-danger"><i className="fa fa-minus-circle me-2"></i>Deductions</h6>
                                        </div>
                                        <div className="card-body p-2">
                                            {/* Statutory Deductions (PF, ESI, PT) - Auto from Salary Master */}
                                            {getStatutoryDeductions(selectedMaster, employeeData[selectedMaster.id]?.workingDays || parseInt(selectedMaster?.working_days) || 30).length > 0 && (
                                                <>
                                                    <div className="mb-2">
                                                        <small className="text-muted fw-semibold">
                                                            <i className="fa fa-shield-alt me-1"></i>Statutory Deductions (Auto)
                                                        </small>
                                                    </div>
                                                    <table className="table table-sm mb-2">
                                                        <tbody>
                                                            {getStatutoryDeductions(selectedMaster, employeeData[selectedMaster.id]?.workingDays || parseInt(selectedMaster?.working_days) || 30).map(item => (
                                                                <tr key={item.id} style={{ background: '#fff5f5' }}>
                                                                    <td>
                                                                        <strong>{item.name}</strong>
                                                                        {item.formula && (
                                                                            <small className="d-block text-muted font-monospace">{item.formula}</small>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ width: '240px' }}>
                                                                        <div className="input-group input-group-sm">
                                                                            <span className="input-group-text bg-danger text-white">₹</span>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control bg-light fw-bold"
                                                                                value={item.amount.toFixed(2)}
                                                                                readOnly
                                                                                title={`Prorated from Gross: Full month ${formatCurrency(item.fullAmount)} ÷ ${item.masterDays}d × ${item.days}d`}
                                                                            />
                                                                        </div>
                                                                        <small className="text-muted d-block mt-1">
                                                                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                                                                                {formatCurrency(item.perDay)}/day × {item.days}d {item.days !== item.masterDays && <>(full: {formatCurrency(item.fullAmount)})</>}
                                                                            </span>
                                                                        </small>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {deductionHeads.length > 0 && <hr className="my-2" />}
                                                </>
                                            )}
                                            
                                            {/* Custom Deduction Heads */}
                                            {deductionHeads.length > 0 && (
                                                <>
                                                    <div className="mb-2">
                                                        <small className="text-muted fw-semibold">
                                                            <i className="fa fa-edit me-1"></i>Other Deductions
                                                        </small>
                                                    </div>
                                                    <table className="table table-sm mb-0">
                                                        <tbody>
                                                            {deductionHeads.map(head => (
                                                                <tr key={head.id}>
                                                                    <td><strong>{head.name}</strong></td>
                                                                    <td style={{ width: '150px' }}>
                                                                        <div className="input-group input-group-sm">
                                                                            <span className="input-group-text bg-danger text-white">₹</span>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                value={employeeData[selectedMaster.id]?.heads?.[head.id] || ''}
                                                                                onChange={(e) => handleHeadAmountChange(selectedMaster.id, head.id, e.target.value)}
                                                                                onWheel={preventScrollChange}
                                                                                placeholder="0"
                                                                                disabled={empAlreadyGenerated}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </>
                                            )}
                                            
                                            {deductionHeads.length === 0 && getStatutoryDeductions(selectedMaster, employeeData[selectedMaster.id]?.workingDays || parseInt(selectedMaster?.working_days) || 30).length === 0 && (
                                                <p className="text-muted text-center mb-0">No deductions</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                        </div>

                            {/* Approved Leaves Section */}
                                        <div className="card mb-3">
                                <div className="card-header py-2" style={{ background: '#d4edda' }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h6 className="mb-0">
                                            <i className="fa fa-calendar-check me-2"></i>
                                            Approved Leaves for {getMonthLabel()}
                                                    </h6>
                                        <span className="badge bg-success">
                                            <i className="fa fa-check me-1"></i>
                                            {employeeLeaves.length} Approved Leave(s)
                                        </span>
                                                </div>
                                            </div>
                                            <div className="card-body p-0">
                                                {loadingLeaves ? (
                                                    <div className="text-center py-3">
                                            <div className="spinner-border spinner-border-sm" role="status"></div>
                                                    </div>
                                                ) : employeeLeaves.length === 0 ? (
                                                    <div className="text-center py-3 text-muted">
                                                        <i className="fa fa-check-circle text-success me-2"></i>
                                            No approved leaves for this month
                                                    </div>
                                                ) : (
                                        <table className="table table-sm mb-0">
                                                            <thead style={{ background: '#f8f9fa' }}>
                                                                <tr>
                                                    <th>#</th>
                                                    <th>Leave Date</th>
                                                    <th>Day</th>
                                                                    <th>Reason</th>
                                                    <th>Applied On</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                {employeeLeaves.map((leave, idx) => (
                                                    <tr key={leave.id} className="table-success">
                                                        <td>{idx + 1}</td>
                                                        <td><strong>{moment(leave.leave_applied).format('DD MMM YYYY')}</strong></td>
                                                                        <td>{moment(leave.leave_applied).format('dddd')}</td>
                                                                        <td>{leave.reason || '-'}</td>
                                                        <td className="text-muted">{moment(leave.created_at).format('DD MMM YYYY')}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                )}
                                            </div>
                                        </div>

                            {/* Generate Button */}
                                        <div className="text-center">
                                {empAlreadyGenerated ? (
                                    <div className="alert alert-success mb-0">
                                        <i className="fa fa-check-circle me-2"></i>
                                        <strong>Invoice Already Generated!</strong>
                                        <span className="ms-2">Net Salary: {formatCurrency(empGeneratedData?.net_salary)}</span>
                                    </div>
                                ) : (
                                            <button
                                        className="btn btn-success btn-lg px-5"
                                        onClick={() => handleGenerateInvoice(selectedMaster.id)}
                                        disabled={generatingFor === selectedMaster.id}
                                    >
                                        {generatingFor === selectedMaster.id ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span>Generating...</>
                                        ) : (
                                            <><i className="fa fa-file-invoice me-2"></i>Generate Invoice for {getMonthLabel()}</>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    );
                })()}

                {/* TABLE VIEW - All Employees */}
                {(
                    <div className="card">
                        <div className="card-header py-2" style={{ background: '#f8f9fa' }}>
                            <h6 className="mb-0"><i className="fa fa-users me-2"></i>All Employees</h6>
                        </div>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status"></div>
                                </div>
                            ) : filteredEmployees.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fa fa-users fa-3x mb-3" style={{ opacity: 0.3 }}></i>
                                    <p>No employees found</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover mb-0" style={{ fontSize: '13px' }}>
                                        <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '50px' }}>#</th>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', minWidth: '150px' }}>Employee</th>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '100px' }}>Per Day</th>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '70px' }} className="text-center" title="Leave / LOP Days">LOP</th>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '80px' }}>Days</th>
                                                {additionHeads.length > 0 && (
                                                    <th colSpan={additionHeads.length} className="text-center bg-success text-white">
                                                        Additions
                                                    </th>
                                                )}
                                                <th colSpan={4} className="text-center text-white" style={{ background: '#c0392b' }}>
                                                    Statutory Deductions
                                                </th>
                                                {deductionHeads.length > 0 && (
                                                    <th colSpan={deductionHeads.length} className="text-center bg-danger text-white">
                                                        Other Deductions
                                                    </th>
                                                )}
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '120px' }} className="text-center bg-primary text-white">
                                                    Net Salary
                                                </th>
                                                <th rowSpan="2" style={{ verticalAlign: 'middle', width: '100px' }} className="text-center">
                                                    Action
                                                </th>
                                            </tr>
                                            <tr>
                                                {additionHeads.map(h => (
                                                    <th key={h.id} className="text-center" style={{ background: '#d4edda', minWidth: '100px' }}>
                                                        {h.name}
                                                    </th>
                                                ))}
                                                <th className="text-center" style={{ background: '#f5b7b1', minWidth: '100px' }}>PF (Emp)</th>
                                                <th className="text-center" style={{ background: '#f5b7b1', minWidth: '100px' }}>ESI (Emp)</th>
                                                <th className="text-center" style={{ background: '#f5b7b1', minWidth: '80px' }}>PT</th>
                                                <th className="text-center" style={{ background: '#f5b7b1', minWidth: '80px' }}>TDS</th>
                                                {deductionHeads.map(h => (
                                                    <th key={h.id} className="text-center" style={{ background: '#f8d7da', minWidth: '100px' }}>
                                                        {h.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredEmployees.map((emp, idx) => {
                                                const totals = calculateEmployeeTotals(emp.id);
                                                const empData = employeeData[emp.id] || { workingDays: parseInt(emp?.working_days) || 30, heads: {} };
                                                const alreadyGenerated = isAlreadyGenerated(emp.user_id);
                                                const generatedData = getGeneratedSalary(emp.user_id);
                                                const editing = isEditing(emp.id);
                                                const isLocked = alreadyGenerated && !editing;
                                                
                                                return (
                                                    <tr key={emp.id} className={isLocked ? 'table-secondary' : (editing ? 'table-warning' : '')}>
                                                        <td className="text-center">{idx + 1}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <strong 
                                                                    style={{ cursor: 'pointer', color: alreadyGenerated ? '#6c757d' : '#0d6efd' }}
                                                                    onClick={() => setSelectedEmployee({ value: emp.id, label: emp.user?.name })}
                                                                    title="Click to view details & leaves"
                                                                >
                                                                    {emp.user?.name} {emp.user?.last_name || ''}
                                                                </strong>
                                                                {alreadyGenerated && (
                                                                    <i className="fa fa-check-circle text-success ms-2" title="Invoice Generated" style={{ fontSize: '13px' }}></i>
                                                                )}
                                                            </div>
                                                            <small className="text-muted">{emp.user?.department?.name || '-'}{emp.emp_type ? ` | ${emp.emp_type}` : ''}</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <strong className="text-primary">
                                                                {formatCurrency(emp.per_day_salary)}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={isLocked ? (generatedData?.leave_days || 0) : (empData.leaveDays || '')}
                                                                onChange={(e) => handleLeaveDaysChange(emp.id, e.target.value)}
                                                                onWheel={preventScrollChange}
                                                                min="0"
                                                                max={parseInt(emp?.working_days) || 31}
                                                                style={{ width: '55px' }}
                                                                disabled={isLocked}
                                                                placeholder="0"
                                                                title="Leave / LOP days"
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <strong style={{ color: (empData.leaveDays > 0) ? '#e67e22' : '#333' }}>
                                                                {isLocked ? (generatedData?.number_working_day || empData.workingDays || 0) : (empData.workingDays || 0)}
                                                            </strong>
                                                            {!isLocked && empData.leaveDays > 0 && (
                                                                <div style={{ fontSize: '10px', color: '#e67e22' }}>
                                                                    {parseInt(emp?.working_days) || 30}-{empData.leaveDays}
                                                                </div>
                                                            )}
                                                        </td>
                                                        {additionHeads.map(head => (
                                                            <td key={head.id} style={{ background: isLocked ? '#e9ecef' : '#f0fff0' }}>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={empData.heads?.[head.id] || ''}
                                                                    onChange={(e) => handleHeadAmountChange(emp.id, head.id, e.target.value)}
                                                                    onWheel={preventScrollChange}
                                                                    placeholder="0"
                                                                    min="0"
                                                                    style={{ minWidth: '80px' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </td>
                                                        ))}
                                                        {/* Statutory Deductions - prorated from Gross (E) based on actual days */}
                                                        {(() => {
                                                            const actualDays = isLocked
                                                                ? (parseInt(generatedData?.number_working_day) || parseInt(empData.workingDays) || parseInt(emp?.working_days) || 30)
                                                                : (parseInt(empData.workingDays) || parseInt(emp?.working_days) || 30);
                                                            const masterDays = parseInt(emp?.working_days) || 30;
                                                            const renderStatCell = (fullAmt, formula, label) => {
                                                                if (!fullAmt || fullAmt <= 0) return <span className="text-muted">-</span>;
                                                                const perDay = fullAmt / masterDays;
                                                                const prorated = perDay * actualDays;
                                                                return (
                                                                    <div>
                                                                        <span className="fw-bold text-danger" title={`Formula: ${formula || '-'} | Full: ${formatCurrency(fullAmt)}`}>
                                                                            {formatCurrency(prorated)}
                                                                        </span>
                                                                        <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                                                                            /{masterDays}d = {formatCurrency(perDay)}/d
                                                                        </small>
                                                                    </div>
                                                                );
                                                            };
                                                            return (
                                                                <>
                                                                    <td style={{ background: '#f5b7b1' }}>
                                                                        {renderStatCell(parseFloat(emp.total_pf_employee), emp.pf_employee_formula, 'PF')}
                                                                    </td>
                                                                    <td style={{ background: '#f5b7b1' }}>
                                                                        {renderStatCell(parseFloat(emp.total_esi_employee), emp.esi_employee_formula, 'ESI')}
                                                                    </td>
                                                                    <td style={{ background: '#f5b7b1' }}>
                                                                        {emp.pt_enabled ? renderStatCell(parseFloat(emp.total_pt), emp.pt_formula, 'PT') : <span className="text-muted">-</span>}
                                                                    </td>
                                                                    <td style={{ background: '#f5b7b1' }}>
                                                                        {renderStatCell(parseFloat(emp.total_tds_deduction), emp.tds_deduction_formula, 'TDS')}
                                                                    </td>
                                                                </>
                                                            );
                                                        })()}
                                                        {deductionHeads.map(head => (
                                                            <td key={head.id} style={{ background: isLocked ? '#e9ecef' : '#fff0f0' }}>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={empData.heads?.[head.id] || ''}
                                                                    onChange={(e) => handleHeadAmountChange(emp.id, head.id, e.target.value)}
                                                                    onWheel={preventScrollChange}
                                                                    placeholder="0"
                                                                    min="0"
                                                                    style={{ minWidth: '80px' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </td>
                                                        ))}
                                                        <td className="text-center" style={{ background: isLocked ? '#d1e7dd' : '#e3f2fd' }}>
                                                            {isLocked ? (
                                                                <>
                                                                    <strong className="text-success fs-6">
                                                                        {formatCurrency(generatedData?.net_salary)}
                                                                    </strong>
                                                                    <small className="d-block text-muted">
                                                                        (Generated)
                                                                    </small>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <strong className={`fs-6 ${editing ? 'text-warning' : 'text-primary'}`}>
                                                                        {formatCurrency(totals.netSalary)}
                                                                    </strong>
                                                                    <small className="d-block text-muted">
                                                                        +{formatCurrency(totals.additions)} -{formatCurrency(totals.deductions)}
                                                                    </small>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                                {isLocked ? (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-warning"
                                                                            onClick={() => handleEditRow(emp.id, emp.user_id)}
                                                                            title="Edit this row"
                                                                        >
                                                                            <i className="fa fa-edit"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => navigate(`/salary_master_edit/${emp.id}`)}
                                                                            title="Edit Salary Master"
                                                                        >
                                                                            <i className="fa fa-cog"></i>
                                                                        </button>
                                                                        <span className="badge bg-success py-2 px-2">
                                                                            <i className="fa fa-check"></i>
                                                                        </span>
                                                                    </>
                                                                ) : editing ? (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-sm btn-success"
                                                                            onClick={() => handleGenerateInvoice(emp.id)}
                                                                            disabled={generatingFor === emp.id}
                                                                            title="Re-generate Invoice"
                                                                        >
                                                                            {generatingFor === emp.id ? (
                                                                                <span className="spinner-border spinner-border-sm"></span>
                                                                            ) : (
                                                                                <><i className="fa fa-sync me-1"></i>Update</>
                                                                            )}
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => handleCancelEdit(emp.id)}
                                                                            title="Cancel editing"
                                                                        >
                                                                            <i className="fa fa-times"></i>
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => navigate(`/salary_master_edit/${emp.id}`)}
                                                                            title="Edit Salary Master"
                                                                        >
                                                                            <i className="fa fa-cog"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-success"
                                                                            onClick={() => handleGenerateInvoice(emp.id)}
                                                                            disabled={generatingFor === emp.id}
                                                                            title="Generate Invoice"
                                                                        >
                                                                            {generatingFor === emp.id ? (
                                                                                <span className="spinner-border spinner-border-sm"></span>
                                                                            ) : (
                                                                                <i className="fa fa-file-invoice"></i>
                                                                            )}
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    </div>
                                )}
                            </div>
                        </div>
                )}

                {/* Info */}
                <div className="card mt-3 bg-light">
                    <div className="card-body py-2">
                        <small className="text-muted">
                            <i className="fa fa-info-circle me-2"></i>
                            <strong>Net Salary</strong> = (Per Day Salary × Working Days) + Additions - (PF Emp + ESI Emp + PT + TDS + Other Deductions)
                            <span className="ms-3">| Deductions are auto-calculated from Salary Master, per-day breakdown shown</span>
                            <span className="ms-3">| Click on employee name to view detailed view with approved leaves</span>
                        </small>
                    </div>
                </div>
            </div>
        </>
    );
}
